<template>
  <base-modal
    class="media_modal"
    @close="closeAddKeywordModal"
  >
    <template #heading>
      Select keywords to add to "{{ selectedPage.url }}"
    </template>

    <custom-search
      placeholder="Filter keywords"
      @search="value => {searchFilter = value}"
    />

    <div class="table_wrap">
      <table class="unassigned_keywords">
        <thead>
          <tr>
            <th>Keyword</th>
            <th>Difficulty</th>
            <th>Search Volume</th>
            <th>Keyword Intent</th>
            <th>Ranking URL</th>
            <th>Ranking Position</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(keyword, index) in sortedKeywords"
            :key="index"
            :class="isSelected(keyword) ? 'selected' : ''"
            @click="toggleSelected(keyword)"
          >
            <td class="keyword-cell">
              {{ keyword.keyword }}
              <svg-vue
                v-if="recommended(keyword)"
                :title="recommended(keyword)"
                icon="icons/review_star"
                class="table_icon tooltipster"
              />
            </td>
            <td>
              <difficulty-tag :difficulty="keyword.difficulty" />
            </td>
            <td>{{ keyword.search_volume > 0 ? keyword.search_volume.toLocaleString() : '< 10' }}</td>
            <td>{{ keyword.query_type }}</td>
            <td>
              {{ keyword.ranking_page }}
              <svg-vue
                v-if="keyword.ranking_mismatch"
                :title="`This keyword ranks position ${keyword.ranking_position} for ${keyword.ranking_page}`"
                icon="icons/link-break"
                class="table_icon tooltipster mismatch"
              />
            </td>
            <td>{{ keyword.ranking_position < 101 ? keyword.ranking_position : '' }}</td>
          </tr>
          <tr v-if="sortedKeywords.length === 0">
            <td
              class="text-center"
              colspan="6"
            >
              You have no unassigned keywords to add to this page. You can choose more keywords on the
              {{ strategy.onboarding_step !== 'COMPLETED' ? ' previous step.' : ' keyword ideas page.' }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <template #footer>
      <primary-button
        v-if="selectedKeywords.length > 0"
        :class="[
          'add_keywords_button',
          processing ? 'disabled' : '',
        ]"
        size="md"
        @click="addKeywords"
      >
        {{ processing ? '' : `Add ${selectedKeywords.length} keywords to page` }}
        <i
          v-if="processing"
          class="fa fa-spin fa-spinner"
        />
      </primary-button>
    </template>
  </base-modal>
</template>

<script>
import _ from 'lodash';
import Swal from 'sweetalert2';
import BaseModal from "../../global/modals/BaseModal";
import CustomSearch from "../../../../views/reports/components/global/forms/CustomSearch";
import {mapActions, mapGetters} from "vuex";
import DifficultyTag from "../../global/assets/DifficultyTag";
import PrimaryButton from "../../global/buttons/PrimaryButton";

export default {
  components: {
    PrimaryButton,
    DifficultyTag,
    BaseModal,
    CustomSearch,
  },
  data() {
    return {
      selectedKeywords: [],
      searchFilter: '',
      dontShowWarningAgain: false,
    }
  },
  computed: {
    ...mapGetters('user', [
        'user'
    ]),
    ...mapGetters('strategy', [
      'strategy',
      'selectedPage',
      'unassignedKeywords',
      'processing',
    ]),
    sortedKeywords() {
      let selectedPagePath = this.removeTrailingSlash(new URL(this.selectedPage.url).pathname);
      let keywords = Object.values(this.unassignedKeywords);
      keywords.forEach((keyword) => {
        let rankingPagePath = keyword.ranking_url
          ? this.removeTrailingSlash(new URL(keyword.ranking_url).pathname)
          : keyword.ranking_url;

        keyword.ranking_match = (selectedPagePath === rankingPagePath);

        let aiAssignedPagePath = keyword.ai_assigned_page
          ? this.removeTrailingSlash(new URL(keyword.ai_assigned_page).pathname)
          : keyword.ai_assigned_page;
        keyword.ai_assigned_match = (selectedPagePath === aiAssignedPagePath);

        keyword.ranking_mismatch = (
            keyword.ranking_url &&
            selectedPagePath !== rankingPagePath &&
            keyword.ranking_position <= 50
        );

        const lowerCaseKeyword = keyword.keyword.toLowerCase();

        const lowerCasePageTitle = this.selectedPage?.pageTitle?.toLowerCase();
        keyword.page_title_match = lowerCasePageTitle?.includes(lowerCaseKeyword);

        const lowerCaseMetaDesc = this.selectedPage?.metaDescription?.toLowerCase()
        keyword.meta_desc_match = lowerCaseMetaDesc?.includes(lowerCaseKeyword);
      });
      if (this.searchFilter){
        keywords = keywords.filter(
            keyword => keyword.keyword.includes(this.searchFilter)
        );
      }
      return _.orderBy(
          keywords,
          ['ranking_match', 'page_title_match', 'meta_desc_match', 'keyword'],
          ['desc', 'desc', 'desc', 'asc'],
      );
    },
  },
  mounted() {
    this.getUser();
  },
  methods: {
    ...mapActions('user', [
        'getUser',
        'turnOffMismatchWarning',
    ]),
    ...mapActions('strategy', [
      'fetchStrategy',
      'addKeywordsToPage',
      'closeAddKeywordModal',
    ]),
    isSelected(keyword) {
      return this.selectedKeywords.findIndex(
          (selectedKeyword) => selectedKeyword.keyword === keyword.keyword
      ) >= 0;
    },
    async addKeywords() {
      let { selectedKeywords } = this;
      const mismatchedKeywords = selectedKeywords.filter((keyword) => keyword.ranking_mismatch).length;
      if (mismatchedKeywords && this.user.show_mismatch_warning) {
        const swalHtml =
          `${mismatchedKeywords === 1 ? 'A selected keyword already ranks ' : 'Some selected keywords already rank '}`+
          'within the top 50 results in Google for a <strong>different page</strong>. <br>'+
          `Are you sure you want to add ${mismatchedKeywords === 1 ? 'it' : 'them'} to this page? <br>`+
          '<label for="swal2-checkbox" class="swal2-checkbox" style="display: flex;">'+
            '<input type="checkbox" id="swal2-checkbox">'+
            '<span class="swal2-label">Don&apos;t show me this again</span>'+
          '</label>';

        Swal.fire({
          icon: 'warning',
          title: 'Mapping mismatch',
          html: swalHtml,
          confirmButtonText: 'Add to page ',
          showCancelButton: true,
          showLoaderOnConfirm: true,
          backdrop: true,
          preConfirm: async () => this.resolveRankingMismatchSwal(selectedKeywords),
          allowOutsideClick: () => !Swal.isLoading()
        }).then((result) => {
          if (result.isConfirmed) {
            Swal.fire({
              icon: 'success',
              title: `Added successfully`,
            })
          }
        });
      } else {
        await this.addKeywordsToPage(selectedKeywords).then(() => {
          const plural = selectedKeywords.length > 1 ? 's' : '';
          this.SweetToast.fire({
            icon: 'success',
            title: `${selectedKeywords.length} keyword${plural} added`
          });
          selectedKeywords = [];
          this.closeAddKeywordModal();
        });
      }
    },
    toggleSelected(keyword) {
      const alreadySelected = this.selectedKeywords.findIndex((selectedKeyword) => {
        return selectedKeyword.keyword === keyword.keyword;
      }) >= 0;
      if (alreadySelected) {
        this.selectedKeywords = this.selectedKeywords.filter((selectedKeyword) => {
          return selectedKeyword.keyword !== keyword.keyword;
        });
      } else {
        this.selectedKeywords.push(keyword);
      }
    },
    recommended(keyword) {
      if (
        keyword.ranking_mismatch ||
        (
          !keyword.ranking_match &&
          !keyword.ai_assigned_match &&
          !keyword.page_title_match &&
          !keyword.meta_desc_match
        )
      ) {
        return null;
      }
      let recommendations = [];
      if (keyword.ranking_match) {
        recommendations.push('This keyword ranks for this page.');
      }
      if (keyword.ai_assigned_match) {
        recommendations.push('This page was recommended by AI.');
      }
      if (keyword.page_title_match) {
        recommendations.push('This keyword is in this page\'s title tag.');
      }
      if (keyword.meta_desc_match) {
        recommendations.push('This keyword is in this page\'s meta description.');
      }

      return 'We recommend assigning this keyword to this page, because:'+
        '<ul>'+
            '<li>'+
                `${recommendations.join('</li><li>')}`+
            '</li>'+
        '</ul>';
    },
    async resolveRankingMismatchSwal(keywords) {
      let dontShowWarnAgain = document.getElementById('swal2-checkbox').checked;
      if (dontShowWarnAgain) {
        await this.turnOffMismatchWarning();
      }
      await this.addKeywordsToPage(keywords).then(response => {
        this.selectedKeywords = [];
        this.SweetToast.fire({
          icon: 'success',
          title: `${keywords.length} ${keywords.length > 1 ? 'keywords' : 'keyword'} added`
        });
        return response;
      }).catch(error => {
        Swal.showValidationMessage(`Request failed: ${error}`)
      });
    },
    removeTrailingSlash(string) {
      if (!string) {
        return null;
      }

      return string === '/' ? string : string.replace(/\/+$/, "");
    }
  },

}
</script>

<style lang="scss" scoped>
:deep(.modal_window) {
  max-width: 1000px;
}
.table_wrap {
  max-height: 50vh;
  overflow-y: scroll;

  .unassigned_keywords {
    width: 100%;

    th, td {
      padding: 1rem;
    }

    th {
      font-size: 16px;
      font-weight: 500;
      line-height: 26px;
      letter-spacing: -0.25px;
      text-align: left;
    }

    tbody tr:nth-of-type(2n+1) {
      background: var(--table-secondary-color);
    }

    tbody tr {
      &.selected {
        background: #F0E9FF;
      }
      &:hover {
        background: #F0E9FF;
        cursor: pointer;

        td:first-of-type {
          color: #5A3CD7;
        }
      }

      td.keyword-cell {
        display: flex;
        align-items: center;
        gap: 5px;
      }
    }

    .table_icon {
      height: 16px;
      width: 16px;

      &.mismatch {
        :deep(path) {
          &[stroke] {
            stroke: var(--negative-color);
          }

          &[fill] {
            fill: var(--negative-color);
          }
        }
      }
    }
  }
}

.add_keywords_button {
  white-space: nowrap;
  align-self: flex-end;
  margin-left: auto;
}


</style>

<style lang="scss">
  input[type="checkbox"] {
    -webkit-appearance: checkbox !important;
  }

  .tooltipster-content ul,
  .tooltipster-content ul li {
      color: inherit;
  }
</style>


